import { maximizeID, minimizeID } from '@shared/utils/uuid-compression';

export const EXTEND_WIDTH = 120;
export const EXTEND_HEIGHT = 40;
export const START_AXIS_Y = 10;
export const BUFFER_BANDWIDTH_WIDTH = 0.2;
export const NB_OFFSET_HEIGHT = 8;
export const BANDWIDTH_HEIGHT = 16;
export const TEXT_X_AXIS_OFFSET = 6;
export const BAR_X_AXIS_OFFSET = 10;
export const START_AXIS_X = 10;

export const COLOR_CONST = {
    red: '#DB4321',
    green: '#B1E471',
    yellow: '#F2EC68',
    red_clearance: '#DBA2AA',
    // Ring-mate protected left turn, painted muted under the through indications.
    left_green: '#6E9C4A',
    left_yellow: '#A99A4E',
    left_red_clearance: '#8E6C73',
    blue: '#293AD2',
    upstream: '#DBA2AA33',
    downstream: '#293AD233',
    border: '#FFFFFF1A',
    nodata: '#565E77',
};

// Widest the signal-name column may grow; longer names are ellipsized by drawText.
export const MAX_NAME_COLUMN_PX = 320;

// Minimum vertical separation between consecutive rows. Junction complexes can carry a 0m (or
// tiny) stored distance between adjacent stops — e.g. a couplet's paired nodes — which would
// stack two rows (bars, offset controls, phase/AOG text) on the same pixel line.
export const MIN_ROW_GAP_PX = 28;

// Bar layers are opaque, so the paint order decides what a viewer sees where two indications overlap.
// The server hands us `colors` as a plain object and its key order puts the muted ring-mate left turn
// after the through green — painting the left OVER the through. Paint by this explicit order instead:
// no-data / red base first, then the muted left, then the vivid through indications on top.
const BAR_LAYER_ORDER = ['nodata', 'red', 'left_green', 'left_yellow', 'left_red_clearance', 'green', 'yellow', 'red_clearance'];
const barLayerIndex = (color) => {
    const index = BAR_LAYER_ORDER.indexOf(color);
    return index === -1 ? BAR_LAYER_ORDER.length : index;
};

// The ring-mate left turn is hatched rather than filled flat, and the hatch leans the way that
// direction's progression bands do — leaning "\" on the downstream rows and "/" on the upstream ones
// — so the bar itself says which way the protected left is moving traffic. Anchored to the canvas origin, so
// the stripes line up down the whole diagram instead of restarting at each interval.
const LEFT_TURN_COLORS = new Set(['left_green', 'left_yellow', 'left_red_clearance']);
export const HATCH_DOWNSTREAM = 1;
export const HATCH_UPSTREAM = -1;
const HATCH_TILE_PX = 6;
const hatch_patterns = new Map();

const hatchPattern = (ctx, color, direction) => {
    const key = `${color}:${direction}`;
    const cached = hatch_patterns.get(key);
    if (cached) return cached;

    const tile = document.createElement('canvas');
    tile.width = HATCH_TILE_PX;
    tile.height = HATCH_TILE_PX;

    const tile_ctx = tile.getContext('2d');
    tile_ctx.strokeStyle = COLOR_CONST[color];
    tile_ctx.lineWidth = 2;
    // Three parallel segments, so the stripe carries on across the tile's edges rather than stopping
    // at them.
    for (const shift of [-HATCH_TILE_PX, 0, HATCH_TILE_PX]) {
        tile_ctx.beginPath();
        if (direction === HATCH_UPSTREAM) {
            tile_ctx.moveTo(shift + HATCH_TILE_PX, 0);
            tile_ctx.lineTo(shift, HATCH_TILE_PX);
        } else {
            tile_ctx.moveTo(shift, 0);
            tile_ctx.lineTo(shift + HATCH_TILE_PX, HATCH_TILE_PX);
        }
        tile_ctx.stroke();
    }

    const pattern = ctx.createPattern(tile, 'repeat');
    hatch_patterns.set(key, pattern);

    return pattern;
};


export const OPTIONS = { 
    fontSize: '11px', 
    fontFamily:'Inter', 
    color: '#fff', 
    textAlign: 'start', 
    textBaseline: 'alphabetic' 
}


export const NOTE_LIST = [
    { color: 'green', text: 'Green Time' },
    { color: 'yellow', text: 'Yellow Time' },
    { color: 'red_clearance', text: 'Red Clearance' },
    { color: 'red', text: 'Red Time' },
    { color: 'left_green', text: 'Left Turn (muted)' },
    { color: 'nodata', text: 'No Data' },
    { color: 'downstream', text: 'SB' },
    { color: 'upstream', text: 'NB' },
];

// A row's bar colors for one direction: nothing when that direction doesn't stop here (crossing /
// pass-through row), a neutral "no data" bar when the signal resolved no plan or measured
// indications, and the real green/red pattern otherwise.
export const getBarColors = (item) => {
    if (!item || item.non_signalized_direction || item.signal_id == null) return null;
    if (item.indication_used === 'none') return { nodata: [[0, (item.plan_cycle || 120) - 1]] };
    return item.colors;
};

const validateNumberInputs = (a, b) => {
    if (typeof a !== 'number' || typeof b !== 'number') {
        throw new Error('Both arguments must be numbers');
    }
};

export const getValueFromPercentage = (total, percent) => {
    validateNumberInputs(total, percent);
    return (total * percent) / 100;
};

export const getPercentage = (total, value) => {
    validateNumberInputs(total, value);
    return (value / total) * 100;
};

export const isValidCoordinates = (coords) => coords.every((coord) => coord >= 0);

export const calculateCoordinates = (cycle, base, offset) => base.map((coord) => coord + offset * cycle);

export const adjustPoint = (point1, point2) => (point1 === point2 ? point2 + BUFFER_BANDWIDTH_WIDTH : point2);

export const transformSpaceTimeData = (data) => {
    if (!data) return {};

    // Initialize return structure with new format
    const result = {
        downstream: {
            signals: [],
            routeData: null
        },
        upstream: {
            signals: [],
            routeData: null
        },
        height: 0,
        width: 0,
        // Keep legacy properties for backward compatibility during transition
        downstreamData: [],
        upstreamData: [],
        route_level_data: null
    };

    // Check if we have an array with route elements (downstream, and usually upstream — a
    // single-direction network has only one route and still renders that direction alone)
    if (Array.isArray(data) && (data.length === 1 || data.length === 2)) {
        // First element (index 0) contains downstream signals and route data
        if (data[0] && Array.isArray(data[0].signals)) {
            const downstreamSignals = data[0].signals;

            // Extract route level data (everything except signals)
            const downstreamRouteData = { ...data[0] };
            delete downstreamRouteData.signals;
            result.downstream.routeData = downstreamRouteData;

            // Initialize height calculation variables
            let downstreamSum = START_AXIS_Y;

            // Calculate distances between signals for scaling
            const distancesBetweenSignals = downstreamSignals
                .map(signal => signal.distance_meters ||
                              signal.downstream_signal_distance_in_meters ||
                              signal.distance_to_next ||
                              signal.distance || 0)
                .filter(d => d && d > 0);

            const totalDistance = distancesBetweenSignals.reduce((sum, x) => sum + x, 0);

            const shortestDistance = distancesBetweenSignals.length > 0 ?
                Math.min(...distancesBetweenSignals) : 100;

            // Calculate division ratio for scaling
            const MINIMUM_HEIGHT = 1024;
            const divisionRatio = (totalDistance < MINIMUM_HEIGHT) ?
                1.5 : (shortestDistance * 0.0241) + 0.5;
            
            // Process downstream signals
            const processedDownstreamSignals = downstreamSignals.map((signal, index) => {
                // Assign height based on accumulated distance
                signal.height = downstreamSum;

                // Get distance to next signal
                const distanceToNext = signal.distance_meters ||
                                      signal.downstream_signal_distance_in_meters ||
                                      signal.distance_to_next ||
                                      signal.distance || 0;

                // Update accumulated height for next signal (never letting two rows share a line)
                if (index < downstreamSignals.length - 1) {
                    downstreamSum += Math.max(distanceToNext / divisionRatio, MIN_ROW_GAP_PX);
                }

                return signal;
            });

            // Store in both new and legacy format
            result.downstreamData = processedDownstreamSignals;
            result.downstream.signals = processedDownstreamSignals;
            
            // Store the final downstream height for container height calculation
            const finalDownstreamHeight = downstreamSum;
            
            // Second element (index 1) contains upstream signals; absent for one-direction networks.
            {
                const upstreamRoute = data[1] && Array.isArray(data[1].signals) ? data[1] : null;
                let upstreamSignals = upstreamRoute ? upstreamRoute.signals : [];

                // Extract route level data (everything except signals)
                if (upstreamRoute) {
                    const upstreamRouteData = { ...upstreamRoute };
                    delete upstreamRouteData.signals;
                    result.upstream.routeData = upstreamRouteData;
                }

                // Initialize upstream height calculation
                let upstreamSum = START_AXIS_Y;

                // Process upstream signals
                const processedUpstreamSignals = upstreamSignals.map((signal, index) => {
                    // Assign height based on accumulated distance
                    signal.height = upstreamSum;

                    // Get distance to next signal (use corresponding downstream distance for consistency)
                    const distanceToNext = index < downstreamSignals.length ?
                        (downstreamSignals[index].distance_meters ||
                         downstreamSignals[index].downstream_signal_distance_in_meters ||
                         downstreamSignals[index].distance_to_next ||
                         downstreamSignals[index].distance || 0) : 0;

                    // Update accumulated height for next signal (mirrors the downstream min gap)
                    if (index < upstreamSignals.length - 1) {
                        upstreamSum += Math.max(distanceToNext / divisionRatio, MIN_ROW_GAP_PX);
                    }

                    return signal;
                });

                // Store in both new and legacy format
                result.upstreamData = processedUpstreamSignals;
                result.upstream.signals = processedUpstreamSignals;
                
                // Calculate container height based on the larger of the two final heights
                const finalUpstreamHeight = upstreamSum;
                const containerHeight = Math.max(finalDownstreamHeight, finalUpstreamHeight) + EXTEND_HEIGHT;

                // Calculate width based on cycle length or use a default
                const cycle = data[0]?.cycle || 100;
                const width = (cycle * 4) + 100 + EXTEND_WIDTH; // Show 4 cycles by default with padding

                // Convert absolute heights to percentages of container height and process bandwidths
                const finalDownstreamData = result.downstreamData.map((signal, index) => {
                    const percentHeight = getPercentage(containerHeight, signal.height);

                    // Process bandwidths if they exist
                    let bandwidths = [];
                    if (signal.bandwidths_coordinates && Array.isArray(signal.bandwidths_coordinates)) {
                        bandwidths = signal.bandwidths_coordinates.map(coords => {
                            return {
                                startCurrentCoordinates: getPercentage(width, coords.startCurrentCoordinates || 0),
                                endCurrentCoordinates: getPercentage(width, coords.endCurrentCoordinates || 0),
                                startUpstreamCoordinates: getPercentage(width, coords.startUpstreamCoordinates || 0),
                                endUpstreamCoordinates: getPercentage(width, coords.endUpstreamCoordinates || 0)
                            };
                        });
                    }

                    const yPos = getValueFromPercentage(containerHeight, percentHeight);

                    return {
                        ...signal,
                        height: percentHeight,
                        bandwidths: bandwidths,
                        yPosition: yPos // Add actual Y position for drawing
                    };
                });

                const finalUpstreamData = result.upstreamData.map((signal, index) => {
                    const percentHeight = getPercentage(containerHeight, signal.height);

                    // Process bandwidths if they exist
                    let bandwidths = [];
                    if (signal.bandwidths_coordinates && Array.isArray(signal.bandwidths_coordinates)) {
                        bandwidths = signal.bandwidths_coordinates.map(coords => {
                            return {
                                startCurrentCoordinates: getPercentage(width, coords.startCurrentCoordinates || 0),
                                endCurrentCoordinates: getPercentage(width, coords.endCurrentCoordinates || 0),
                                startUpstreamCoordinates: getPercentage(width, coords.startUpstreamCoordinates || 0),
                                endUpstreamCoordinates: getPercentage(width, coords.endUpstreamCoordinates || 0)
                            };
                        });
                    }

                    const yPos = getValueFromPercentage(containerHeight, percentHeight);

                    return {
                        ...signal,
                        height: percentHeight,
                        bandwidths: bandwidths,
                        yPosition: yPos // Add actual Y position for drawing
                    };
                });

                // Update both new and legacy formats
                result.downstreamData = finalDownstreamData;
                result.upstreamData = finalUpstreamData;
                result.downstream.signals = finalDownstreamData;
                result.upstream.signals = finalUpstreamData;

                // Create legacy route_level_data array from extracted route data
                result.route_level_data = [
                    result.downstream.routeData,
                    result.upstream.routeData
                ].filter(Boolean); // Remove any null/undefined entries

                // Set final dimensions
                result.height = containerHeight;
                result.width = width;
            }
        }
    }

    return result;
};

// Function to create an array of color segments
const createColorsArray = (colors, interval, cycle, name_short) => {
    const result = [];
    const totalSegments = Math.floor(interval / cycle) ; // Calculate total segments
    const transformedArray = [];

    // Transform input colors into an array of objects (start, end, color)
    Object.keys(colors).forEach((color) => {
        colors[color].forEach(([start, end]) => {
            transformedArray.push({ start, end, color });
        });
    });

    // Sort array by start position
    transformedArray.sort((a, b) => a.start - b.start);

    // Generate segments for each cycle
    for (let i = 0; i < totalSegments; i++) {
        const cycleOffset = i * cycle;
        transformedArray.forEach((segment) => {
            result.push({
                start: getPercentage(interval, segment.start + cycleOffset),
                end: getPercentage(interval, segment.end + cycleOffset),
                color: segment.color,
            });
        });
    }

    return result;
};

const createStreamDataArray = (currents, upstreams, interval, cycle) => {
    const result = [];
    const totalSegments = Math.floor(interval / cycle);

    const processSegment = (current, upstream, i) => {
        const currentCoordinates = calculateCoordinates(cycle, current, i);
        const upstreamCoordinates = calculateCoordinates(cycle, upstream, i);

        if (isValidCoordinates([...currentCoordinates, ...upstreamCoordinates])) {
            result.push({
                startCurrentCoordinates: getPercentage(interval, currentCoordinates[0]),
                endCurrentCoordinates: getPercentage(interval, currentCoordinates[1]),
                startUpstreamCoordinates: getPercentage(interval, upstreamCoordinates[0]),
                endUpstreamCoordinates: getPercentage(interval, upstreamCoordinates[1]),
            });
        }
    };

    const processCurrentUpstreamPair = (current, upstream) => {
        if (current?.length === 2 && upstream?.length === 2) {
            for (let i = 0; i <= totalSegments; i++) {
                processSegment(current, upstream, i);
            }
        }
    };

    currents?.forEach((current, index) => {
        const upstream = upstreams[index];
        processCurrentUpstreamPair(current, upstream);
    });

    return result;
};

export const drawAxis = (ctx, x = 100, y) => {
    ctx.beginPath();
    ctx.lineWidth = 1; // Adjust the border width
    ctx.strokeStyle = COLOR_CONST.border; // Adjust the border color
    ctx.moveTo(x, 0); // Starting point (top-left corner)
    ctx.lineTo(x, y); // Draw line to the bottom-left corner
    ctx.lineTo(y, y); // Draw line to the bottom-right corner
    ctx.stroke();
    ctx.closePath();
};

export const drawText = (ctx, text, x, yPosition, containerHeight, offsetHeight = 10, options = {}) => {
    if (text == null || text === '') return;
    const {fontSize, fontFamily, color, textAlign, textBaseline, maxWidthPx} = Object.assign({}, OPTIONS, options);

    // Set font properties
    ctx.font = `${fontSize} ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textAlign = textAlign;
    ctx.textBaseline = textBaseline;

    // Ellipsize to fit the name column instead of overflowing into the diagram.
    let finalText = String(text);
    if (maxWidthPx && ctx.measureText(finalText).width > maxWidthPx) {
        while (finalText.length > 1 && ctx.measureText(`${finalText}…`).width > maxWidthPx) {
            finalText = finalText.slice(0, -1);
        }
        finalText = `${finalText.trimEnd()}…`;
    }

    // Draw the text on the canvas
    ctx.beginPath();

    // Use the yPosition directly
    const finalY = yPosition + offsetHeight;

    ctx.fillText(finalText, x, finalY);
    ctx.closePath();
};

export const drawColoredLine = (
    ctx,
    colors,
    containerWidth,
    containerHeight,
    height,
    offsetHeight = 0,
    startPosition = START_AXIS_X,
    lineHeight = 6,
    plan_cycle = 100, // Default to 100 if not provided
    hatchDirection = 0
) => {
    if (!colors) {
        console.warn('No colors data provided to drawColoredLine');
        return;
    }

    // Check if colors is an object with color properties (red, green, etc.)
    if (typeof colors === 'object' && !Array.isArray(colors)) {
        
        // Use the provided plan_cycle instead of hardcoded 100
        // A zero / missing cycle would make the repetition count Infinity below and lock the tab up in the
        // draw loop, which looks exactly like the page hanging. There is nothing to tile without a cycle.
        const cycle = plan_cycle;
        if (!(cycle > 0)) return;

        // Calculate how many repetitions needed to fill the container width
        // Add extra repetitions to ensure we cover the entire visible area
        const visibleWidth = containerWidth - startPosition;
        const repetitions = Math.max(4, Math.ceil(visibleWidth / cycle) + 1);
        
        // Process each color type (red, green, yellow, etc.)
        Object.keys(colors).sort((a, b) => barLayerIndex(a) - barLayerIndex(b)).forEach(colorType => {
            // Skip non-array properties
            if (!Array.isArray(colors[colorType])) {
                return;
            }
            
            // Process each interval for this color
            colors[colorType].forEach(interval => {
                if (Array.isArray(interval) && interval.length === 2) {
                    const [start, end] = interval;

                    // Repeat the pattern for multiple cycles
                    for (let i = 0; i < repetitions; i++) {
                        const cycleOffset = i * cycle;

                        const adjustedStartX = start + cycleOffset + startPosition;
                        const adjustedEndX = end + cycleOffset + startPosition;
                        
                        // Draw this segment
                        drawLine(
                            ctx,
                            colorType,
                            adjustedStartX,
                            adjustedEndX,
                            height + offsetHeight,
                            lineHeight,
                            hatchDirection
                        );
                    }
                }
            });
        });
    } else if (Array.isArray(colors)) {
        // Handle the case where colors is already an array of segments
        colors.forEach((segment, index) => {
            if (!segment || !segment.color || segment.start === undefined || segment.end === undefined) {
                console.warn(`Invalid segment at index ${index}:`, segment);
                return;
            }

            const startX = getValueFromPercentage(containerWidth - startPosition, segment.start) + startPosition;
            const width = getValueFromPercentage(containerWidth - startPosition, segment.end - segment.start);
            
            drawLine(
                ctx,
                segment.color,
                startX,
                startX + width,
                height + offsetHeight,
                lineHeight,
                hatchDirection
            );
        });
    }
};

const drawLine = (ctx, color, startX, endX, y, lineHeight, hatchDirection = 0) => {
    if (!COLOR_CONST[color]) {
        console.warn(`Unknown color: ${color}`);
        return;
    }

    const width = endX - startX + 1; // Add 1 pixel to width to eliminate seams

    ctx.beginPath();
    if (hatchDirection && LEFT_TURN_COLORS.has(color)) {
        // A wash under the stripes keeps the interval's extent readable where the hatch is sparse.
        ctx.fillStyle = `${COLOR_CONST[color]}40`;
        ctx.fillRect(startX, y, width, lineHeight);
        ctx.fillStyle = hatchPattern(ctx, color, hatchDirection);
    } else {
        ctx.fillStyle = COLOR_CONST[color];
    }
    ctx.fillRect(startX, y, width, lineHeight);
    ctx.closePath();
};

export const drawStreamData = (
    ctx,
    topLeft,
    topRight,
    bottomRight,
    bottomLeft,
    topY,       // Renamed from topHeight to clarify it's a Y position
    bottomY,    // Renamed from bottomHeight to clarify it's a Y position
    color,
    containerWidth,
    containerHeight,
    offsetWidth = 0,
    offsetHeight = 0
) => {
    // Fix the offsetWidth if it's not finite
    if (!isFinite(offsetWidth)) {
        console.warn('offsetWidth is not finite, using start_axis_x + BAR_X_AXIS_OFFSET');
        offsetWidth = START_AXIS_X + BAR_X_AXIS_OFFSET;
    }
    
    // Calculate coordinates
    const createPoint = (containerWidth, offsetWidth, yPosition, value) => {
        return {
            x: offsetWidth + value,
            y: yPosition
        };
    };

    // Create points for the polygon
    const topLeftPoint = createPoint(containerWidth, offsetWidth, topY, topLeft);
    const topRightPoint = createPoint(containerWidth, offsetWidth, topY, topRight);
    const bottomRightPoint = createPoint(containerWidth, offsetWidth, bottomY, bottomRight);
    const bottomLeftPoint = createPoint(containerWidth, offsetWidth, bottomY, bottomLeft);

    // Draw the polygon
    ctx.beginPath();
    ctx.moveTo(topLeftPoint.x, topLeftPoint.y);
    ctx.lineTo(topRightPoint.x, topRightPoint.y);
    ctx.lineTo(bottomRightPoint.x, bottomRightPoint.y);
    ctx.lineTo(bottomLeftPoint.x, bottomLeftPoint.y);
    ctx.closePath();

    // Fill with color
    ctx.fillStyle = color;
    ctx.fill();

    // Draw border
    ctx.strokeStyle = COLOR_CONST.border;
    ctx.stroke();
};


export const isObjectNull = (object) => {
    if (!object) return true;
    if (Object.keys(object).length === 0) {
        return true;
    }

    return false;
};

// Pointer position relative to the offset editor's canvas. `root` scopes the lookup to one editor
// instance — a bare document.getElementById picks whichever is first in the DOM, which is wrong as soon
// as an inline editor and a modal one are both mounted.
export const getMousePos = (e, root) => {
    const canvas = root?.querySelector('#canvas_fullscreen') ?? document.getElementById('canvas_fullscreen');
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
    };
};

export const getSignalWithPos = (mousePos, containerHeight, downstreamData) => {
    let rs = false;
    const y = mousePos.y;
    downstreamData?.map((downstream) => {
        const heightItem = getValueFromPercentage(containerHeight, downstream.height);
        if (heightItem < y && y < heightItem + BANDWIDTH_HEIGHT) {
            rs = downstream;
        }
    });

    return rs;
};

export const getTextMaxWidthPixels = (ctx, textArray, options = {}) => {

    const {fontSize, fontFamily, color, textAlign, textBaseline} = Object.assign({}, OPTIONS, options)

    // Set font properties
    ctx.font = `${fontSize} ${fontFamily}`;
    ctx.fillStyle = color;
    ctx.textAlign = textAlign;
    ctx.textBaseline = textBaseline;

    const textMaxWidthPixels = Math.round(
        Math.max(
            ...textArray?.map(text => ctx.measureText(text).width)
        )
    )

    return textMaxWidthPixels
}

// Calculate the exact width needed based on the farthest bandwidth coordinate
export const calculateRequiredWidth = (data, startAxisX = START_AXIS_X) => {
    if (!data) return 0;
    
    let maxEndpoint = 0;
    let maxCycle = 100; // Default cycle length
    
    // Find the maximum endpoint in all bandwidth coordinates
    const processSignals = (signals) => {
        if (!signals) return;
        
        signals.forEach(signal => {
            // Store the cycle length for later use
            if (signal.plan_cycle) {
                maxCycle = Math.max(maxCycle, signal.plan_cycle);
            }
            
            // Check bandwidths
            if (signal.bandwidths_coordinates && Array.isArray(signal.bandwidths_coordinates)) {
                signal.bandwidths_coordinates.forEach(bandwidth => {
                    // Each bandwidth is an array of points
                    bandwidth.forEach(points => {
                        // Each point is an array of coordinates
                        points.forEach(coord => {
                            maxEndpoint = Math.max(maxEndpoint, coord);
                        });
                    });
                });
            }
            
            // Also check individual bandwidths property
            if (signal.bandwidths && Array.isArray(signal.bandwidths)) {
                signal.bandwidths.forEach(bandwidth => {
                    if (bandwidth.endCurrentCoordinates) {
                        maxEndpoint = Math.max(maxEndpoint, bandwidth.endCurrentCoordinates);
                    }
                    if (bandwidth.endUpstreamCoordinates) {
                        maxEndpoint = Math.max(maxEndpoint, bandwidth.endUpstreamCoordinates);
                    }
                });
            }
        });
    };
    
    // Process both downstream and upstream data - handle both new and legacy structure
    processSignals(data.downstream?.signals || data.downstreamData);
    processSignals(data.upstream?.signals || data.upstreamData);
    
    // Add the start axis position
    // Add a small buffer (half a cycle) to show a bit of the next cycle
    
    return Math.min(startAxisX + maxEndpoint + (maxCycle / 2), 4096);
};

// ---- Optimus indications ------------------------------------------------------------------------
// An Optimus job's TSD payload draws its bars from the probability of green — a band appears where the
// job puts the odds of green at 90% or better — so it carries only two colours and its edges are not the
// programmed switch points. The plan the job returned carries the rest: each phase's split, yellow and
// all-red, its ring, and the order the ring runs.
//
// Two ways to draw a bar, and the expanded diagram switches between them:
//   'actual'     — the bars exactly as the job produced them. Only the left turn's PHASE is read off the
//                  plan, for the Left Turns column; nothing is painted onto a probability band, because
//                  its edges are not switch points and anything hung off them lands in the wrong place.
//   'programmed' — the whole ring laid out from the optimized timing plan: every phase in turn holding
//                  its split as green, then yellow, then red clearance, anchored on the plan's offset.

// Bands are drawn in whole seconds against a cycle the payload writes as cycle - 1, so "the same instant"
// has to allow a second and a half of slack.
const INDICATION_EPSILON = 1.5;

// Intervals are drawn within one cycle, so one that runs past the end comes back at the start.
const wrapInterval = (start, length, cycle) => {
    if (!(length > 0) || !(cycle > 0)) return [];

    const from = ((start % cycle) + cycle) % cycle;
    const to = from + length;

    return to <= cycle ? [[from, to]] : [[from, cycle], [0, to - cycle]];
};

const sameInstant = (a, b, cycle) =>
    Math.abs(((((a % cycle) + cycle) % cycle) - (((b % cycle) + cycle) % cycle) + cycle + cycle / 2) % cycle - cycle / 2) <
    INDICATION_EPSILON;

// Where a phase's green actually begins: the interval no other green interval runs into.
const greenStart = (greens, cycle) => {
    const head = greens.find(
        (interval, index) => !greens.some((other, other_index) => other_index !== index && sameInstant(other[1], interval[0], cycle)),
    );

    return (head ?? greens[0])[0];
};

// The protected left that shares a barrier with a through phase, by the standard NEMA pairing: the
// through phases are even and each is served with the odd phase below it (2 with 1, 4 with 3, and so on).
// Matching on ring and barrier instead would pick whichever phase happened to be listed first whenever a
// controller leaves `barrier` unset, which is how a through ends up paired with the wrong movement.
// Whether a phase runs its left protected or permitted, from the movements the plan maps onto it —
// `edge_connection_mapping` entries are [edge_connection_id, phase_type], the same field the split
// analysis reads for its Prot/Perm column. Null when the phase carries no movement at all, which says
// the phase is not in this plan rather than anything about how the left runs.
const leftTurnType = (phase) => {
    const types = (phase?.edge_connection_mapping || []).map((entry) => entry?.[1]).filter(Boolean);
    if (!types.length) return null;
    if (types.some((type) => type === 'protected' || type === 'prot_perm')) return 'protected';
    if (types.some((type) => type === 'permitted')) return 'permitted';

    return null;
};

const leftTurnPhase = (plan, phase_number) => {
    const number = Number(phase_number);
    if (!plan?.tms_data || !Number.isFinite(number) || number % 2 !== 0) return null;

    const left_number = number - 1;
    const phase = plan.tms_data[String(left_number)];
    if (!phase || phase.dummy_phase || phase.pedestrian_phase) return null;

    const type = leftTurnType(phase);
    if (!type) return null;

    return { phase_number: left_number, phase, type, split: splitOfPhase(plan, left_number) };
};

// A phase's split lives on `phase_data` — `tms_data` carries the rest of the plan (ring, barrier, order,
// yellow, all-red) but leaves `split` null.
const splitOfPhase = (plan, number) => plan?.phase_data?.[String(number)]?.split ?? null;

// The phase order the job was submitted with: `offset_constraints.sequence.rings` from the job details,
// as [[{type:'phase',phase:1},{type:'phase',phase:2},{type:'barrier'},…], […]]. The ring holding a phase
// gives that phase's position, which is what says whether a left turn leads or lags.
const sequenceRingFor = (sequence, phase_number) => {
    for (const ring of sequence?.rings || []) {
        const order = ring.filter((entry) => entry?.type === 'phase').map((entry) => Number(entry.phase));
        if (order.includes(Number(phase_number))) return order;
    }

    return null;
};

// Which of the sequence's rings holds a phase — the Left Turns override names one order per ring, in
// the order the rings are listed.
const sequenceRingIndexFor = (sequence, phase_number) => {
    const rings = sequence?.rings || [];
    for (let index = 0; index < rings.length; index += 1) {
        const order = rings[index].filter((entry) => entry?.type === 'phase').map((entry) => Number(entry.phase));
        if (order.includes(Number(phase_number))) return index;
    }

    return null;
};

// The lead/lag the Left Turns column is asking for on this phase's ring, or null when it is asking for
// nothing this bar can act on.
const leftTurnOverrideFor = (sequence, override, phase_number, ring) => {
    if (!override) return null;

    const ring_index = sequenceRingIndexFor(sequence, phase_number) ?? (ring === 2 ? 1 : 0);
    const value = String(override).split('-')[ring_index];

    return value === 'lead' || value === 'lag' ? value : null;
};

// Re-sequence a through phase and its protected left within the block the two of them share.
//
// The block's own boundaries are held: the barrier behind the pair does not move because a left changed
// sides, and neither does anything else in the ring. So the phase that now leads starts where the block
// starts and the other follows it — which means the THROUGH shifts by the left's split when the left goes
// from lead to lag, rather than the left hopping over a pinned through.
//
// Only a pair that runs back to back has a lead or a lag to swap; anything else is left as the plan has it.
const resequenceLeftTurn = (starts, phases, through_index, left_index, desired, splitOf) => {
    if (Math.abs(through_index - left_index) !== 1) return false;
    if ((left_index < through_index ? 'lead' : 'lag') === desired) return false;

    const block_start = starts[Math.min(through_index, left_index)];
    const lead_index = desired === 'lead' ? left_index : through_index;
    const lag_index = desired === 'lead' ? through_index : left_index;

    starts[lead_index] = block_start;
    starts[lag_index] = block_start + splitOf(phases[lead_index]);

    return true;
};

// Every phase in a ring that runs, in the order the plan runs them — the job's own sequence when it has
// one, and the controller's sequence index otherwise.
const ringPhases = (plan, ring, order) => {
    const position = (number) => {
        const index = order?.indexOf(Number(number)) ?? -1;
        return index === -1 ? null : index;
    };

    return Object.entries(plan?.tms_data || {})
        .map(([number, phase]) => ({ number: Number(number), phase, split: splitOfPhase(plan, number) }))
        .filter(
            (entry) =>
                entry.phase &&
                !entry.phase.dummy_phase &&
                !entry.phase.pedestrian_phase &&
                (order ? position(entry.number) !== null : entry.phase.ring === ring) &&
                entry.split > 0,
        )
        .sort((a, b) => {
            const by_sequence = (position(a.number) ?? Infinity) - (position(b.number) ?? Infinity);
            if (order && by_sequence !== 0) return by_sequence;

            return (a.phase.sequence_index ?? a.number) - (b.phase.sequence_index ?? b.number);
        });
};

// Which of a through phase's ring runs first, its left turn or itself — read from the plan the job
// returned rather than estimated from the bars.
const leftTurnOrder = (plan, through_number, left_number, order, left) => {
    // A permitted left runs with its through, so it has no lead or lag to state.
    if (left?.type === 'permitted') return 'permitted';
    // Protected, but the job returned no split for it: it is ordered somewhere, we just cannot say where.
    if (!(left?.split > 0)) return null;

    if (order) {
        const left_position = order.indexOf(Number(left_number));
        const through_position = order.indexOf(Number(through_number));
        if (left_position !== -1 && through_position !== -1) return left_position < through_position ? 'lead' : 'lag';
    }

    // No sequence from the job, so fall back to the controller's own phase ordering.
    const through = plan?.tms_data?.[String(through_number)];
    const left_phase = plan?.tms_data?.[String(left_number)];
    if (!through || !left_phase) return null;
    if (left_phase.sequence_index == null || through.sequence_index == null) return null;

    return left_phase.sequence_index < through.sequence_index ? 'lead' : 'lag';
};

// The bars as the job returned them: left untouched. Only the left turn's identity and order come from
// the plan, so the Left Turns column has something true to show.
export const buildOptimusIndications = (signal, plan, splitDeltaFor, sequence, override) => {
    const left = leftTurnPhase(plan, signal?.phase_number);
    if (!left) return null;

    const planned = leftTurnOrder(
        plan,
        signal.phase_number,
        left.phase_number,
        sequenceRingFor(sequence, signal.phase_number),
        left,
    );
    // A permitted left has no order to re-sequence, so the column keeps saying so.
    const desired =
        planned === 'permitted'
            ? null
            : leftTurnOverrideFor(sequence, override, signal.phase_number, plan?.tms_data?.[String(signal.phase_number)]?.ring);

    return {
        colors: signal.colors,
        left_turn: { phase_number: left.phase_number },
        left_turn_order: desired ?? planned,
    };
};

// The whole bar laid out from the optimized timing plan.
//
// Every phase in the through phase's ring is placed end to end in its programmed order, each one holding
// its split: green first, then yellow, then red clearance. The run is anchored on the coordinated phase,
// whose green starts at the plan's offset, and red fills whatever is left of the cycle. Only this
// approach's own two movements are drawn on its bar — the through and its protected left.
export const buildProgrammedIndications = (signal, plan, splitDeltaFor, sequence, override) => {
    // The cycle the bar is TILED with, not the one the plan names — the renderer repeats the pattern
    // every `plan_cycle`, so laying the ring out against anything else smears it across the bar.
    const cycle = signal?.plan_cycle ?? plan?.cycle;
    const through = plan?.tms_data?.[String(signal?.phase_number)];
    const greens = signal?.colors?.green;
    if (!through || !(cycle > 0) || !Array.isArray(greens) || !greens.length) return null;

    const left = leftTurnPhase(plan, signal.phase_number);
    const order = sequenceRingFor(sequence, signal.phase_number);
    const phases = ringPhases(plan, through.ring, order);
    const through_index = phases.findIndex((entry) => entry.number === Number(signal.phase_number));
    if (through_index === -1) return null;

    const splitOf = ({ number, split }) => split + (splitDeltaFor?.(signal.signal_id, number) || 0);

    // Anchored on the through phase's own green, which is already in the diagram's time base — the plan's
    // offset is measured from the controller's reference point, not from where this bar starts. A green
    // that crosses the end of the cycle arrives as two intervals; the one another green runs into is the
    // tail of that same green, not the start of the phase.
    const anchor = greenStart(greens, cycle);
    const starts = new Array(phases.length);
    starts[through_index] = anchor;
    for (let step = 1; step < phases.length; step++) {
        const index = (through_index + step) % phases.length;
        const previous = (through_index + step - 1 + phases.length) % phases.length;
        starts[index] = starts[previous] + splitOf(phases[previous]);
    }

    const left_index = left?.split > 0 ? phases.findIndex((entry) => entry.number === left.phase_number) : -1;
    // The Left Turns column asking for the other order moves the pair inside its own block, so the bar
    // shows the sequence the plan would actually run rather than the one it was submitted with.
    const desired = leftTurnOverrideFor(sequence, override, signal.phase_number, through.ring);
    const resequenced =
        desired && left_index !== -1
            ? resequenceLeftTurn(starts, phases, through_index, left_index, desired, splitOf)
            : false;

    const colors = { red: [[0, cycle]], green: [], yellow: [], red_clearance: [] };
    const paint = (entry, start, keys) => {
        const yellow = entry.phase.yellow || 0;
        const all_red = entry.phase.red || 0;
        const green_length = Math.max(splitOf(entry) - yellow - all_red, 0);

        colors[keys.green].push(...wrapInterval(start, green_length, cycle));
        colors[keys.yellow].push(...wrapInterval(start + green_length, yellow, cycle));
        colors[keys.red_clearance].push(...wrapInterval(start + green_length + yellow, all_red, cycle));
    };

    paint(phases[through_index], starts[through_index], {
        green: 'green',
        yellow: 'yellow',
        red_clearance: 'red_clearance',
    });

    if (left_index !== -1) {
        colors.left_green = [];
        colors.left_yellow = [];
        colors.left_red_clearance = [];
        paint(phases[left_index], starts[left_index], {
            green: 'left_green',
            yellow: 'left_yellow',
            red_clearance: 'left_red_clearance',
        });
    }

    return {
        colors,
        left_turn: left ? { phase_number: left.phase_number } : null,
        left_turn_order: left
            ? (resequenced ? desired : leftTurnOrder(plan, signal.phase_number, left.phase_number, order, left))
            : null,
    };
};

// Applies the derived indications across every route of an Optimus job's TSD payload, in place —
// the caller already deep-copied it away from the job.
export const applyOptimusIndications = (routes, split_output, splitDeltaFor, source, sequences, overrides) => {
    if (!Array.isArray(routes) || !split_output) return routes;

    // Ids reach this function in whichever form the payload they came from used: the job details minimize
    // them to 22-char base62, while the update-offsets endpoint answers with raw uuids. Index the plan
    // under both so a row finds it either way round.
    const plan_by_signal = new Map();
    for (const [signal_id, plan] of Object.entries(split_output)) {
        for (const form of [signal_id, minimizeID(signal_id), maximizeID(signal_id)]) {
            if (form) plan_by_signal.set(form, plan);
        }
    }

    const build = source === 'programmed' ? buildProgrammedIndications : buildOptimusIndications;

    for (const route of routes) {
        for (const signal of route?.signals || []) {
            // This runs in place, and a source switch runs it again on the same rows — so the bars the
            // payload itself carried are kept aside, and every build starts from them rather than from
            // the indications the previous build painted on.
            if (!signal.payload_indications) {
                signal.payload_indications = {
                    colors: signal.colors,
                    left_turn: signal.left_turn ?? null,
                    left_turn_order: signal.left_turn_order ?? null,
                };
            }
            const base = { ...signal, colors: signal.payload_indications.colors };
            const derived =
                build(
                    base,
                    plan_by_signal.get(signal?.signal_id),
                    splitDeltaFor,
                    sequences?.[signal?.signal_id],
                    overrides?.[signal?.signal_id],
                ) ?? signal.payload_indications;

            signal.colors = derived.colors;
            signal.left_turn = derived.left_turn;
            signal.left_turn_order = derived.left_turn_order;
        }
    }

    return routes;
};
